import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import {
  SimulationProvider,
  buildInitialState,
  simulationReducer,
  useSimulation,
} from '../state'
import { TaskPanel } from './TaskPanel'
import { PluginPanel } from './PluginPanel'
import { SubmitBar } from './SubmitBar'
import { useSaveProgress } from '../useSaveProgress'
import { client } from '@Utils'
import { simulationEndpoints } from '@URLS'

/**
 * Inner component rendered inside the SimulationProvider so it can
 * call useSimulation() and access dispatch.
 */
const SimulationInner = ({ inputState, onAllComplete, activityPk, coursenum, chainState, completedResults, canRetry, resumeState, onStateChange, hideTaskPanel }) => {
  const [state] = useSimulation()
  const hasTasks = state.tasks.length > 0

  // Auto-save hook (no-ops when activityPk is falsy)
  useSaveProgress(state, chainState, activityPk, coursenum)

  // Notify parent (chain) of state changes for persistence.
  // The chain gates on a Continue button before advancing, so the completed
  // state is safe to propagate — it won't pollute the next sim's snapshot.
  useEffect(() => {
    if (onStateChange) {
      onStateChange(state)
    }
  }, [state, onStateChange])

  // Hide framework only when showing completed results (post-submission).
  // In standalone mode, keep panels visible after all tasks complete so
  // students can still see and interact with their work.
  const hideFramework = !!completedResults

  return (
    <>
      {hideTaskPanel ? (
        // Chain mode: parent provides the framework wrapper and task panel
        <div className={`simulation-plugin-panel-wrapper${hideFramework ? ' simulation-complete' : ''}`}>
          {!hideFramework && (
            <PluginPanel inputState={inputState} onAllComplete={onAllComplete} resumeState={resumeState} />
          )}
        </div>
      ) : (
        // Standalone mode: render full framework with task panel
        <div className={`simulation-framework${hideFramework ? ' simulation-complete' : ''}`}>
          {hasTasks && !hideFramework && <TaskPanel />}
          {!hideFramework && (
            <PluginPanel inputState={inputState} onAllComplete={onAllComplete} resumeState={resumeState} />
          )}
        </div>
      )}
      {activityPk && (
        <SubmitBar
          activityPk={activityPk}
          coursenum={coursenum}
          state={state}
          chainState={chainState}
          completedResults={completedResults}
          canRetry={canRetry}
        />
      )}
    </>
  )
}

SimulationInner.propTypes = {
  inputState: PropTypes.object,
  onAllComplete: PropTypes.func,
  activityPk: PropTypes.string,
  coursenum: PropTypes.string,
  chainState: PropTypes.object,
  completedResults: PropTypes.object,
  canRetry: PropTypes.bool,
  resumeState: PropTypes.object,
  onStateChange: PropTypes.func,
  hideTaskPanel: PropTypes.bool,
}

/**
 * Root component for a single simulation.
 *
 * When activityPk is provided (activity mode), loads saved progress
 * on mount, wires up auto-save, and shows a submit bar on completion.
 */
export const SimulationApp = ({ config, inputState, onAllComplete, chainState, restoreState, resumeState, onStateChange, hideTaskPanel }) => {
  const activityPk = config.activityPk || null
  const coursenum = config.coursenum || ''
  const [initialState, setInitialState] = useState(null)
  const [completedResults, setCompletedResults] = useState(null)
  const [canRetry, setCanRetry] = useState(false)
  const [loading, setLoading] = useState(!!activityPk)

  useEffect(() => {
    // In chain mode with a saved snapshot, restore directly
    if (!activityPk && restoreState) {
      setInitialState(restoreState)
      return
    }

    if (!activityPk) {
      setInitialState(buildInitialState(config))
      return
    }

    // Load saved progress from backend
    client
      .post(simulationEndpoints.loadProgress({ coursenum, activityPk }))
      .then(res => {
        const saved = res.data
        if (saved && saved.state && Object.keys(saved.state).length > 0 && saved.status !== 'completed') {
          // Restore from saved state, backfilling guid from config if missing (legacy snapshots)
          const restored = { ...saved.state }
          if (!restored.guid) restored.guid = config.guid || ''
          setInitialState(restored)
        } else {
          setInitialState(buildInitialState(config))
        }
        // Capture completed results for display
        if (saved && saved.completedResults) {
          setCompletedResults(saved.completedResults)
        }
        if (saved && saved.canRetry) {
          setCanRetry(true)
        }
      })
      .catch(() => {
        setInitialState(buildInitialState(config))
      })
      .finally(() => {
        setLoading(false)
      })
  }, [])

  if (loading || !initialState) {
    return <div className="simulation-framework"><p>Loading...</p></div>
  }

  return (
    <SimulationProvider initialState={initialState} reducer={simulationReducer}>
      <SimulationInner
        inputState={inputState}
        onAllComplete={onAllComplete}
        activityPk={activityPk}
        coursenum={coursenum}
        chainState={chainState}
        completedResults={completedResults}
        canRetry={canRetry}
        resumeState={resumeState}
        onStateChange={onStateChange}
        hideTaskPanel={hideTaskPanel}
      />
    </SimulationProvider>
  )
}

SimulationApp.defaultProps = {
  inputState: null,
  onAllComplete: null,
  chainState: null,
  restoreState: null,
  resumeState: null,
  onStateChange: null,
  hideTaskPanel: false,
}

SimulationApp.propTypes = {
  config: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    plugin: PropTypes.string,
    pluginConfig: PropTypes.object,
    tasks: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        description: PropTypes.string,
        completionCriteria: PropTypes.object,
      })
    ),
    activityPk: PropTypes.string,
    coursenum: PropTypes.string,
  }).isRequired,
  inputState: PropTypes.object,
  onAllComplete: PropTypes.func,
  chainState: PropTypes.object,
  restoreState: PropTypes.object,
  resumeState: PropTypes.object,
  onStateChange: PropTypes.func,
  hideTaskPanel: PropTypes.bool,
}
